import { memo, useState, useMemo, type KeyboardEvent, type MouseEvent, useEffect } from 'react'
import { Button, ButtonGroup, Cell, Tooltip, useKeyboardInteraction } from '@highlight/ui'
import { COMPLETED_ACTION_DURATION_MS, type StagedAction } from '../hooks/use-staged-actions'
import { useStagedActionStatus, useStagedActionCallbacks } from '../hooks/use-staged-action-view'
import { actionPreviewProps, actionStatusRing, useActionRowModel } from '../hooks/staged-actions/use-action-row-model'
import { InvokeIcon } from '@renderer/components/invoke-icon'
import { CopyIconButton } from '@renderer/overlay/components/copy-icon-button'
import { ReconnectButton } from './action-connection-error'
import { SidepanelCellClose } from './cells/close'
import { SIDEPANEL_CELL_TITLE } from './cells/surface'
import { getActionIcon } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { ActionIconSlot } from '@renderer/components/action-icon-slot'
import { ActionSource } from './action-source'
import { ActionWarning } from '@renderer/components/action-warning'
import { ActionTagArea } from '@renderer/components/action-tags'
import { applyMetadataTransitions, visibleNestedViews } from '@renderer/components/action-tags/status-transition'
import { ViewLongRunningActionCell } from './view-long-running-action-cell'
import { isLongRunningAction } from '@renderer/hooks/use-long-running-action'
import { ActionContentPreview } from '@renderer/components/action-content-preview'
import { CommenterMeta, hasCommenterMeta } from '@renderer/components/commenter-meta'
import { NestedView } from '@renderer/components/nested-view'
import { askUserCardVariant, getAnswerableFraming } from '@renderer/chat/integrations/answerable'
import { AskUserBadge } from '@renderer/chat/integrations/ask-user-card'
import { getReportOriginSkill, OriginSkillIcon } from '@renderer/chat/integrations/report-to-user'
import { PrerequisiteCtaButton } from '../hooks/staged-actions/use-connection-prerequisite'
import { NO_ATTACHMENTS, NO_NESTED_VIEWS } from '../hooks/staged-actions/empty'
import { ActionErrorBadge, ActionErrorNote, ActionSuccessBadge } from './action-status-badge'
import { CollapsedActionRow } from './collapsed-action-row'
import { ActionRowContextMenuItems, ActionRowHoverButtons } from './action-row-actions'
import { CellContextMenu } from './cell-actions'
import { CellPreviewCard, HOVER_CARD_TRIGGER } from './cell-preview-card'
import { ActionPreview } from './action-preview'
import { trackStagedActionCopy, trackStagedActionOpenLink } from '../hooks/staged-actions/track-staged-action'

export interface ViewActionCellProps {
  action: StagedAction
  sourceLabel?: string
  /** Opt in to the hover preview card — the Outbox list (Home and the full-list View), not nested views. */
  hoverPreview?: boolean
  /** Whether long body content fades out as a list preview. Defaults to true. */
  clampContent?: boolean
}

interface ViewActionCellContentProps extends ViewActionCellProps {
  /**
   * When this action's expiry window started, on the same clock the removal
   * timer uses. Passed down rather than re-derived so the countdown the row
   * draws can't drift from the moment the row actually disappears.
   */
  expiryStartTime: string | null
}

function ViewActionCellContent({
  action,
  sourceLabel = 'Home',
  hoverPreview,
  clampContent = true,
  expiryStartTime,
}: ViewActionCellContentProps) {
  const isKeyboard = useKeyboardInteraction()
  // Deliberately not reading `pendingStartTime` here: it's the client success
  // marker, which is a *later* instant than the expiry actually runs from.
  // `expiryStartTime` comes down from the parent instead.
  const model = useActionRowModel(action, { sourceLabel, eventSource: 'cell' })
  const {
    view,
    isAnswerable,
    isReport,
    status,
    unread,
    isArchived,
    isInvoking,
    isInvokingThis,
    isExecuted,
    isUndoingThis,
    actionHasError,
    hasUndoError,
    canUndo,
    inArchiveWindow,
    errorMessage,
    connectionError,
    unsatisfiedPrerequisite,
    ctaLabel,
    connectPrerequisite,
    warning,
    handleInvoke,
    handleUndo,
    handleUndoArchive,
    handleClear,
  } = model
  const icon = useMemo(() => getActionIcon(action), [action])
  // A report attributed to a skill is badged with that skill's icon rather than
  // the integration glyph — the skill is what produced the message.
  const reportSkill = getReportOriginSkill(action)

  const [isFocused, setIsFocused] = useState(false)
  const [ctxOpen, setCtxOpen] = useState(false)

  if (!view) return null

  const isStaged = status === 'staged'
  const attachments = view.attachments ?? NO_ATTACHMENTS
  const nestedViews = view.nestedViews ?? NO_NESTED_VIEWS
  const hasContent =
    view.tags.length > 0 || !!view.content || nestedViews.length > 0 || hasCommenterMeta(view.contentMeta)

  /** @param focusContent land with the caret at the end of the body editor (hover card's edit affordance). */
  const openActionView = (focusContent = false, trigger?: string) => {
    setIsFocused(false)
    model.openActionView(focusContent, trigger)
  }

  const handleClick = (event: MouseEvent) => {
    setIsFocused(false)
    model.openActionView(false, undefined, event)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.metaKey && e.key === 'Enter' && !isArchived) {
      e.preventDefault()
      e.stopPropagation()
      if (unsatisfiedPrerequisite) {
        connectPrerequisite()
        return
      }
      // The first link button shows a cmd+enter chip whenever
      // isExecuted || hasUndoError; mirror that condition here so the
      // chip's promise matches what the handler does.
      if ((isExecuted || hasUndoError) && view.buttons[0]) {
        trackStagedActionOpenLink(action, { source: 'cell', trigger: 'hotkey' })
        window.open(view.buttons[0].link, '_blank')
      } else if (!isExecuted && !hasUndoError && !isInvoking && !isReport) {
        handleInvoke()
      }
    }
    if (e.metaKey && e.key === 'z' && (canUndo || inArchiveWindow)) {
      e.preventDefault()
      e.stopPropagation()
      // Mutually exclusive today (the archive window only ever wraps a staged
      // row), but keyed off one branch so a keypress can never do both.
      if (canUndo) handleUndo()
      else handleUndoArchive()
    }
  }

  // Outbox rows collapse to their title, with every control moving to the
  // floating preview (Figma 6726:50553).
  //
  // `collapsed` must stay stable for as long as the row is in the list —
  // invoking from the card flips transient flags, and a row that re-expanded
  // under the pointer mid-click would be its own bug.
  //
  // A question collapses with everything else (HIG-5813): it was the one row
  // still shipping its framing and its Answer button inline, which made the
  // single item that only needs one tap the tallest thing in the Outbox. The
  // card carries both now — framing in the body slot, Answer as the primary.
  //
  // A failed action stays collapsed too (HIG-5848). Inflating the row into a
  // full card with a danger banner made one failure the loudest thing in the
  // Outbox; now the row keeps its shape, a red badge replaces the corner icon,
  // and the message plus its retry live in the card. Connection errors ride the
  // same path — the card's primary becomes the Reconnect/Retry split — so the
  // old `!connectionError` guard here is gone rather than left dead (a
  // connection error already implied `error` status, so it never fired).
  //
  // An executed action is the same story with the outcome inverted (HIG-5860):
  // sending used to inflate the row into its widest form — comment card and a
  // two-button footer — at the exact moment there is least left to decide. It
  // now keeps the row, swaps in a green check, runs the expiry countdown around
  // the tile, and hands Undo / the result link to the card. Undo failures are
  // deliberately *not* here: `undo-error` clears `isExecuted`, so the row
  // re-expands to carry its own retry.
  //
  // Invoking is collapsible for the reason it once wasn't: it used to need
  // in-cell progress the collapsed row didn't carry. The row carries it now —
  // the status ring — so expanding mid-invoke would only inflate and collapse
  // the row either side of a state the user never acts on. The card follows it
  // there rather than blanking out, so hovering a running action still answers
  // what it is; the footer just has nothing left to offer but a spinner.
  //
  // Archived is the same thin row in the Archived filter: the expanded cell was
  // just a read-only draft with an empty footer, so the card (body + open, no
  // Edit/Invoke/Archive) already covers it. Undo-error stays expanded — it is
  // the one state whose Retry lives on the row itself.
  const isRowCollapsible = isStaged || isInvoking || actionHasError || isExecuted || isArchived
  const collapsed = hoverPreview === true && isRowCollapsible

  const statusRing = actionStatusRing(model, expiryStartTime)
  const iconSlotProps = {
    overlay: statusRing,
    unread: unread && !isExecuted && !actionHasError && !hasUndoError,
    subIcon: isExecuted ? <ActionSuccessBadge /> : actionHasError || hasUndoError ? <ActionErrorBadge /> : <></>,
  }

  const cell = collapsed ? (
    <CollapsedActionRow
      action={action}
      title={view.title ?? action.title}
      unread={unread}
      hasError={actionHasError}
      subIcon={isExecuted ? <ActionSuccessBadge /> : undefined}
      iconOverlay={statusRing}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        if (isKeyboard()) setIsFocused(true)
      }}
      onBlur={() => setIsFocused(false)}
      focused={isFocused}
    />
  ) : (
    <Cell
      className="rounded-xl"
      isPending={isExecuted}
      focused={isFocused}
      onClick={handleClick}
      onFocus={() => {
        if (isKeyboard()) setIsFocused(true)
      }}
      onBlur={() => setIsFocused(false)}
      onKeyDown={handleKeyDown}
    >
      {!isArchived && <SidepanelCellClose onClick={() => handleClear()} />}

      <Cell.Header showChevron={false}>
        {/* No integration behind an ask-user action, so the row wears the badge
            of the card it opens. Same slot, so it lines up with its neighbours. */}
        {isAnswerable ? (
          <AskUserBadge variant={askUserCardVariant(action)} {...iconSlotProps} />
        ) : (
          <ActionIconSlot view={action.view} {...iconSlotProps}>
            {reportSkill ? <OriginSkillIcon skillName={reportSkill} /> : icon}
          </ActionIconSlot>
        )}
        {/* Title + source row grouped as one column so the origin (meeting,
              chat, source doc) always reads as attribution for the title
              above it, whether the action is still staged or already sent. */}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Cell.Title className={SIDEPANEL_CELL_TITLE}>{view.title ?? action.title}</Cell.Title>
          <ActionSource action={action} />
        </div>
      </Cell.Header>

      {isAnswerable ? (
        // The cell preview frames *why* the question exists (which run paused),
        // not the question or its options — those live in the drawer card.
        <Cell.Content>
          <p className="text-foreground-secondary type-prose-sm line-clamp-2">{getAnswerableFraming(action)}</p>
        </Cell.Content>
      ) : (
        hasContent && (
          <Cell.Content>
            {visibleNestedViews(nestedViews).map((nested, idx) => (
              <NestedView
                key={idx}
                view={nested.view}
                preview
                onButtonAction={(target) =>
                  target === 'open link'
                    ? trackStagedActionOpenLink(action, { source: 'cell', surface: 'nested-view' })
                    : trackStagedActionCopy(action, 'link', { source: 'cell', surface: 'nested-view' })
                }
              />
            ))}
            {view.tags.length > 0 && (
              <ActionTagArea
                tags={applyMetadataTransitions(view.tags, nestedViews)}
                tagOrder={view.tagOrder}
                singleLine
              />
            )}
            <CommenterMeta contentMeta={view.contentMeta}>
              {view.content && (
                <ActionContentPreview content={view.content} attachments={attachments} clamp={clampContent} />
              )}
            </CommenterMeta>
          </Cell.Content>
        )
      )}

      {/* One quiet line rather than Cell.Banner's tinted, bordered, icon-led
          block: the footer already carries Retry, so the error only has to say
          what happened. `Report Issue` is gone from the cell — reporting now
          goes through the toolbar's feedback modal, the same route every other
          surface uses. */}
      {(connectionError || actionHasError) && (
        <ActionErrorNote onClick={(e) => e.stopPropagation()}>
          <span className="min-w-0 flex-1">{errorMessage || 'Something went wrong. Try again.'}</span>
          {connectionError && <ReconnectButton action={action} stopPropagation />}
        </ActionErrorNote>
      )}

      {hasUndoError && (
        <ActionErrorNote onClick={(e) => e.stopPropagation()}>
          <span className="min-w-0 flex-1">{errorMessage || 'Failed to undo. Try again.'}</span>
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation()
              handleUndo()
            }}
          >
            Retry
          </Button>
        </ActionErrorNote>
      )}

      {/* min-w-0: Cell.Footer is a flex child of Cell, so its default
            min-width:auto lets `truncate`'s nowrap content push it wider than
            the cell instead of ellipsizing. */}
      <Cell.Footer className="min-w-0">
        <div className="flex w-full min-w-0 items-center justify-between gap-2">
          <div className="text-foreground-secondary type-ui-sm flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
            {!isExecuted && warning && <ActionWarning warning={warning} />}
            {canUndo && (
              <Tooltip>
                <Tooltip.Trigger
                  render={
                    <Button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleUndo()
                      }}
                      variant="ghost"
                      disabled={isUndoingThis}
                      loading={isUndoingThis}
                    >
                      Undo
                    </Button>
                  }
                />
                <Tooltip.Content>
                  <Tooltip.Hotkey hotkey="cmd+z" />
                </Tooltip.Content>
              </Tooltip>
            )}
          </div>

          {!isArchived &&
            !isExecuted &&
            !hasUndoError &&
            !isInvoking &&
            (!!unsatisfiedPrerequisite || !connectionError) && (
              <div className="flex shrink-0 items-center gap-2">
                {unsatisfiedPrerequisite ? (
                  <PrerequisiteCtaButton label={ctaLabel} isFocused={isFocused} onClick={connectPrerequisite} />
                ) : (
                  <div className="flex items-center gap-1.5">
                    {hasContent && view.content && !isAnswerable && (
                      <CopyIconButton
                        value={view.content}
                        icon="content"
                        ariaLabel="Copy content"
                        stopPropagation
                        onCopy={() => trackStagedActionCopy(action, 'content', { source: 'cell' })}
                      />
                    )}
                    {!isReport && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleInvoke()
                        }}
                        variant="primary"
                        tone={actionHasError ? 'danger' : 'neutral'}
                        disabled={isInvokingThis}
                        loading={isInvokingThis}
                        left={<InvokeIcon icon={view.invokeIcon} />}
                        hotkey={isFocused ? 'cmd + enter' : undefined}
                      >
                        {actionHasError ? 'Retry' : view.invokeLabel}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

          {isInvoking && (
            <Button variant="outline" disabled loading>
              {view.invokeLabel}
            </Button>
          )}

          {(isExecuted || hasUndoError) && (
            <div className="flex items-center gap-2">
              {hasContent && view.content && (
                <CopyIconButton
                  value={view.content}
                  icon="content"
                  ariaLabel="Copy content"
                  stopPropagation
                  onCopy={() => trackStagedActionCopy(action, 'content', { source: 'cell' })}
                />
              )}
              {view.buttons.map((button, index) => (
                <ButtonGroup key={button.link} variant="outline">
                  <CopyIconButton
                    value={button.link}
                    icon="link"
                    ariaLabel={`Copy link for ${button.label}`}
                    stopPropagation
                    onCopy={() => trackStagedActionCopy(action, 'link', { source: 'cell' })}
                  />
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation()
                      trackStagedActionOpenLink(action, { source: 'cell' })
                      window.open(button.link, '_blank')
                    }}
                    hotkey={isFocused && index === 0 ? 'cmd + enter' : undefined}
                  >
                    {button.label}
                  </Button>
                </ButtonGroup>
              ))}
            </div>
          )}
        </div>
      </Cell.Footer>
    </Cell>
  )

  const menu = <ActionRowContextMenuItems model={model} />

  if (!hoverPreview) {
    return (
      <CellContextMenu items={menu}>
        <div className="group/action relative">{cell}</div>
      </CellContextMenu>
    )
  }

  const previewProps = actionPreviewProps(
    model,
    action,
    () => openActionView(true, HOVER_CARD_TRIGGER),
    () => openActionView(false, HOVER_CARD_TRIGGER),
  )

  return (
    <CellContextMenu items={menu} onOpenChange={setCtxOpen}>
      <CellPreviewCard
        // `group/cell` too so pointer-on-cluster keeps the collapsed row's
        // `group-hover/cell:` chevron reveal alive (cluster is the cell's sibling).
        triggerClassName="group/cell group/action relative"
        // No card over an archive counting down — the row is on its way out, and
        // its only live control (Undo) is already on the cluster. No card over an
        // open context menu either: it would sit on top of the menu.
        disabled={!collapsed || inArchiveWindow || ctxOpen}
        preview={previewProps && <ActionPreview {...previewProps} />}
      >
        {cell}
        <ActionRowHoverButtons model={model} collapsed={collapsed} />
      </CellPreviewCard>
    </CellContextMenu>
  )
}

export const ViewActionCell = memo(function ViewActionCell(props: ViewActionCellProps) {
  const action = props.action
  const isLongRunning = isLongRunningAction(action)

  const { markActionExpired } = useStagedActionCallbacks()
  // Client-success start time for this-window invokes (set on invoke success,
  // mirrored to peers). Anchors the expiry to the same instant as the progress
  // bar so they agree; falls back to server fields on reload/cross-window.
  const { pendingStartTime: livePendingStartTime } = useStagedActionStatus(action)

  // When metadata.status is set with statusUpdatedAt it's authoritative — that
  // covers shell/CC actions ('pending') as well as server-set 'staged'/'error'
  // states that should suppress the transient expiry path.
  const hasMetadataStatus = !!action.metadata?.status && !!action.metadata?.statusUpdatedAt

  // Persisted pending: shell/CC actions whose resolution lifecycle is tracked
  // server-side via metadata.status. Survives reload; statusUpdatedAt may be
  // reset (e.g. when CC resolves) to restart the expiry window.
  const persistedPending = hasMetadataStatus && action.metadata?.status === 'pending'

  // Transient pending: instant actions (Slack/Linear/etc.) that just invoked
  // successfully. No metadata is written — expiry is driven from the live
  // client-success marker, falling back to updatedAt when it's absent.
  const transientPending =
    !hasMetadataStatus && !!action.lastInvokedAt && !(action.outputData as { isError?: boolean } | null)?.isError

  const isPending = persistedPending || transientPending
  const pendingStartTime = persistedPending
    ? (action.metadata?.statusUpdatedAt ?? null)
    : transientPending
      ? (livePendingStartTime ?? action.updatedAt)
      : null

  // The one clock the expiry runs on — both the removal timer below and the
  // countdown the row draws read it.
  //
  // `pendingStartTime` first, and `resolvedAt` only as the fallback. They look
  // interchangeable and are not: `resolvedAt` is stamped server-side the moment
  // the action executes, while `pendingStartTime` is when the *result* became
  // visible here, a round-trip later. Anchoring on `resolvedAt` spent the first
  // seconds of the window before the user could see anything to undo. Long-
  // running actions are the exception — they have no client-success marker, and
  // `resolvedAt` is genuinely when their result appears.
  const expiryStartTime = isLongRunning ? action.resolvedAt : (pendingStartTime ?? action.resolvedAt)

  // Unified expiry timer for all action types.
  // Long-running actions only expire once resolved.
  useEffect(() => {
    if (isLongRunning ? !action.resolvedAt : !isPending) return
    if (!expiryStartTime) return

    const startMs = new Date(expiryStartTime).getTime()
    const remaining = COMPLETED_ACTION_DURATION_MS - (Date.now() - startMs)

    if (remaining <= 0) {
      markActionExpired(action.id)
      return
    }

    const timer = setTimeout(() => {
      markActionExpired(action.id)
    }, remaining)

    return () => clearTimeout(timer)
  }, [isLongRunning, action.resolvedAt, isPending, expiryStartTime, action.id, markActionExpired])

  // `hover:z-10`: `[contain:layout]` makes every row its own stacking context,
  // so without raising the hovered one its floating button cluster paints
  // underneath the next row.
  if (isLongRunning) {
    return (
      <div className="relative w-full [contain:layout] focus-within:z-10 hover:z-10">
        <ViewLongRunningActionCell action={action} sourceLabel={props.sourceLabel} />
      </div>
    )
  }

  return (
    <div className="relative w-full [contain:layout] focus-within:z-10 hover:z-10">
      <ViewActionCellContent {...props} expiryStartTime={expiryStartTime} />
    </div>
  )
})
